import React from 'react';
import { SxProps, TextField } from '@mui/material';
import { sendApiRequest } from 'lib/api/public';
import { Autocomplete, CircularProgress } from '@mui/material';
import { debounce } from '@mui/material/utils';
import notify from 'lib/notify';
import { Candidate } from './types';

type CandidatesApiResponse = {
  list: Candidate[];
};

type AutocompleteService = {
  getCandidatesApiMethod: (input: string) => Promise<CandidatesApiResponse>;
};

const autocompleteService: { current: AutocompleteService | null } = { current: null };

const getCandidatesApiMethod = (search: string) => {
  return sendApiRequest<CandidatesApiResponse>({ path: `/candidates/list?search=${search}`, method: 'get' });
};

type Props = {
  onSelect: (candidate: Candidate | null) => void;
  label?: string;
  defaultValue?: Candidate;
  textFieldStyle?: React.CSSProperties;
  inputPropStyle?: React.CSSProperties;
  helperText?: string;
  sx?: SxProps;
  error?: boolean;
  disabled?: boolean;
  excludedCandidateId?: string;
  slotProps?: React.ComponentProps<typeof Autocomplete<Candidate>>['slotProps'];
};

function CandidateAutocomplete({
  label,
  onSelect,
  defaultValue,
  textFieldStyle,
  inputPropStyle,
  helperText,
  sx,
  error,
  disabled = false,
  excludedCandidateId,
  slotProps,
}: Props) {
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState<Candidate | null>(defaultValue || null);

  React.useEffect(() => {
    setValue(defaultValue ?? null);
  }, [defaultValue]);

  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<Candidate[]>([]);

  const fetch = React.useMemo(
    () =>
      debounce((request: { input: string }, callback: (results?: Candidate[]) => void) => {
        setLoading(true);
        const service = autocompleteService.current;

        if (!service) {
          setLoading(false);
          callback([]);
          return;
        }

        service
          .getCandidatesApiMethod(request.input)
          .then((data: CandidatesApiResponse) => {
            setLoading(false);
            callback(data.list || []);
          })
          .catch(error => {
            notify(error);
            setLoading(false);
            callback([]);
          });
      }, 400),
    [],
  );

  const setOptionsFromFetch = (results: Candidate[]) => {
    let newOptions: Candidate[] = [];

    if (results) {
      newOptions = [...results].filter(option => option._id !== excludedCandidateId);
    }

    // Add current value if it's not already in the results
    if (value && value._id !== excludedCandidateId && !newOptions.find(opt => opt._id === value._id)) {
      newOptions = [value, ...newOptions];
    }

    setOptions(newOptions);
  };

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      autocompleteService.current = {
        getCandidatesApiMethod,
      };
    }

    fetch({ input: inputValue }, (results?: Candidate[]) => {
      if (active) {
        setOptionsFromFetch(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      sx={sx}
      slotProps={slotProps}
      isOptionEqualToValue={(option, value) => option._id === value._id}
      renderOption={(props, option) => {
        const { key, ...restProps } = props as typeof props & { key: React.Key };
        return (
          <li key={key} {...restProps}>
            <span>{`${option.firstName} ${option.lastName}`}</span>
            <span
              style={{
                display: 'inline-block',
                marginLeft: '5px',
                fontSize: '11px',
                color: '#666',
                fontStyle: 'italic',
              }}
            >
              {option.primaryEmail}
            </span>
          </li>
        );
      }}
      disabled={disabled}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      getOptionLabel={option => `${option.firstName || ''} ${option.lastName || ''}`}
      noOptionsText="Type a candidate name ..."
      onChange={(_event, newValue: Candidate | null) => {
        if (excludedCandidateId && newValue?._id === excludedCandidateId) {
          setValue(null);
          onSelect(null);
          return;
        }

        setValue(newValue);
        onSelect(newValue);
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={params => (
        <TextField
          {...params}
          id="candidate-auto-complete"
          size="small"
          sx={textFieldStyle}
          helperText={helperText}
          label={label}
          error={error}
          InputLabelProps={{
            style: {
              top: '-5px',
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            style: inputPropStyle,
          }}
        />
      )}
    />
  );
}

export default CandidateAutocomplete;
